import { nanoid } from 'nanoid';
import { initDB } from '../db';
import type { ScheduleRepository } from './ScheduleRepository';
import type { Schedule, Milestone } from '@/types/schedule';

const ARCHIVE_AFTER_DAYS = 30;

export class IndexedDBScheduleRepository implements ScheduleRepository {
  async getActiveSchedules(): Promise<Schedule[]> {
    const db = await initDB();
    const all = await db.getAll('schedules');
    return all
      .filter((s) => !s.archived)
      .sort((a, b) => new Date(a.bakeDate).getTime() - new Date(b.bakeDate).getTime());
  }

  async getSchedule(id: string): Promise<Schedule | undefined> {
    const db = await initDB();
    return db.get('schedules', id);
  }

  async createSchedule(bakeDate: string, milestones: Milestone[]): Promise<Schedule> {
    const db = await initDB();
    const schedule: Schedule = {
      id: nanoid(),
      bakeDate,
      milestones,
      archived: false,
      createdAt: new Date().toISOString(),
      syncStatus: 'local',
      source: 'app',
    };
    await db.put('schedules', schedule);
    return schedule;
  }

  async updateSchedule(id: string, updates: Partial<Schedule>): Promise<Schedule> {
    const db = await initDB();
    const existing = await db.get('schedules', id);
    if (!existing) throw new Error(`Schedule ${id} not found`);

    const updated = { ...existing, ...updates, id };
    await db.put('schedules', updated);
    return updated;
  }

  async deleteSchedule(id: string): Promise<void> {
    const db = await initDB();
    await db.delete('schedules', id);
  }

  async completeMilestone(scheduleId: string, milestoneId: string): Promise<Schedule> {
    const db = await initDB();
    const schedule = await db.get('schedules', scheduleId);
    if (!schedule) throw new Error(`Schedule ${scheduleId} not found`);

    schedule.milestones = schedule.milestones.map((m) =>
      m.id === milestoneId ? { ...m, completed: true, completedAt: new Date().toISOString() } : m,
    );
    await db.put('schedules', schedule);
    return schedule;
  }

  async archiveOldSchedules(): Promise<void> {
    const db = await initDB();
    const all = await db.getAll('schedules');
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - ARCHIVE_AFTER_DAYS);

    const tx = db.transaction('schedules', 'readwrite');
    for (const schedule of all) {
      if (!schedule.archived && new Date(schedule.bakeDate) < cutoff) {
        await tx.store.put({ ...schedule, archived: true });
      }
    }
    await tx.done;
  }
}
